// Default for rewrite and the other AutoWrite actions that share this picker
// (FAQs). The backend also defaults page-draft prompts to Sonnet 5.5.
export const DEFAULT_REWRITE_MODEL = 'claude-sonnet-5-5';

export const REWRITE_GPT_MODEL_STORAGE_KEY = 'qwa.rewriteGptModel';

// The default leads, then by provider, strongest first.
export const REWRITE_MODEL_OPTIONS = [
  { text: 'Claude Sonnet 5.5 (Anthropic)', value: DEFAULT_REWRITE_MODEL },
  { text: 'GPT-6.1 Sol (OpenAI)', value: 'gpt-6.1-sol' },
  { text: 'GPT-6 Luna (OpenAI)', value: 'gpt-6-luna' },
  { text: 'Gemini 3.8 Flash', value: 'gemini-3.8-flash' },
];

// Session storage can still hold a GPT-5.6 id from before the 2026-09-23 swap,
// a Claude Sonnet 5 id from before Sonnet 5.5 replaced it on 2026-09-28, or a
// GPT-6 Sol id from before GPT-6.1 Sol replaced it on 2026-10-01. There is no
// GPT-6 Terra; Terra follows Sol. One lookup, no chaining, so every Sol id points
// straight at GPT-6.1 Sol. Unknown ids, including Gemini 3.1 Pro (dropped
// 2026-09-29), are left alone so the catalog check below falls back to the default.
const LEGACY_REWRITE_MODEL_REMAP = {
  'gpt-5.6': 'gpt-6.1-sol',
  'gpt-5.6-sol': 'gpt-6.1-sol',
  'gpt-5.6-terra': 'gpt-6.1-sol',
  'gpt-5.6-luna': 'gpt-6-luna',
  'gpt-6-sol': 'gpt-6.1-sol',
  'claude-sonnet-5': 'claude-sonnet-5-5',
};

function canonicalizeRewriteModel(id) {
  if (typeof id !== 'string') return '';
  const trimmed = id.trim();
  return LEGACY_REWRITE_MODEL_REMAP[trimmed] || trimmed;
}

export function isRewriteCatalogId(id) {
  return REWRITE_MODEL_OPTIONS.some((option) => option.value === id);
}

export function readStoredRewriteGptModel() {
  try {
    if (typeof sessionStorage === 'undefined') {
      return DEFAULT_REWRITE_MODEL;
    }
    const id = canonicalizeRewriteModel(sessionStorage.getItem(REWRITE_GPT_MODEL_STORAGE_KEY));
    if (isRewriteCatalogId(id)) {
      return id;
    }
  } catch (error) {
    // sessionStorage can throw in private mode
  }
  return DEFAULT_REWRITE_MODEL;
}

export function writeStoredRewriteGptModel(id) {
  if (!isRewriteCatalogId(id)) {
    return;
  }
  try {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(REWRITE_GPT_MODEL_STORAGE_KEY, id);
  } catch (error) {
    // sessionStorage can throw in private mode
  }
}

export function rewriteModelLabel(id) {
  return (
    REWRITE_MODEL_OPTIONS.find((option) => option.value === id)?.text ||
    REWRITE_MODEL_OPTIONS.find((option) => option.value === DEFAULT_REWRITE_MODEL).text
  );
}

export function gptModelForRewriteRequest(selectedId) {
  const fromArg = canonicalizeRewriteModel(selectedId);
  if (isRewriteCatalogId(fromArg) && fromArg !== DEFAULT_REWRITE_MODEL) {
    return fromArg;
  }
  const stored = readStoredRewriteGptModel();
  if (isRewriteCatalogId(stored) && stored !== DEFAULT_REWRITE_MODEL) {
    return stored;
  }
  if (isRewriteCatalogId(fromArg)) {
    return fromArg;
  }
  return DEFAULT_REWRITE_MODEL;
}

export function streamRewriteTextBody(payload) {
  const gptModel = gptModelForRewriteRequest(payload?.gptModel);
  const rest = payload && typeof payload === 'object' ? { ...payload } : {};
  delete rest.gptModel;
  return { gptModel, ...rest };
}
